import { useTranslation } from 'react-i18next';
import SmallLetter from '../../../../../components/SmallLetter/SmallLetter';
import { IInitialState } from '../../../../../reduxService';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { getActionByProjectType } from '../../../../actionMap';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { ISteppedGabionResults } from '../../../api/IApiResponse';
import { selectStillingBasinAndSoilDataByProject } from '../../../selector/conversion';

interface IBasinDesignSteppedProps {}

const BasinDesignStepped = (props: IBasinDesignSteppedProps) => {
    const { t } = useTranslation();
    const { selectResults, selectProject } = getActionByProjectType();
    const results: IInitialState<ISteppedGabionResults> = useAppSelector(selectResults as any);
    const stillingBasinSoilData = useAppSelector(selectStillingBasinAndSoilDataByProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    return (
        <div className='d-flex flex-column justify-content-center align-items-center gap-3 h-85 w-100 static-results'>
            <table className='tableSteppedBasinDesign'>
                <thead>
                    <tr>
                        <th>{t('output_static_vertical_stepped_basin_design')}</th>
                        <th>{t('output_symbol')}</th>
                        <th>{t('output_value')}</th>
                        <th>{t('output_unit')}</th>
                        <th>{t('output_check')}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className='bg-white'>
                        <td>{t('output_static_stepped_min_length_crest')}</td>
                        <td>
                            <SmallLetter normal='L' small='crest' />
                        </td>
                        <td>{roundToTwo(results.data?.basinDesign.minLengthOfTheCrest)}</td>
                        <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            <div className='d-flex justify-content-center'>-</div>
                        </td>
                    </tr>
                    <tr>
                        <td>{t('output_static_stepped_min_length_stilling_basin')}</td>
                        <td>
                            <SmallLetter normal='L' small='bac' />
                        </td>
                        <td>{roundToTwo(results.data?.basinDesign.minLengthOfTheStillingPool)}</td>
                        <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            {stillingBasinSoilData?.stillingBasin?.poolLength! >= roundToTwo(results.data?.basinDesign.minLengthOfTheStillingPool) ? (
                                <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />
                            ) : (
                                <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />
                            )}
                        </td>
                    </tr>
                    <tr className='bg-white'>
                        <td>{t('output_static_stepped_stilling_basin_width')}</td>
                        <td>
                            <SmallLetter normal='W' small='b' />
                        </td>
                        <td>{roundToTwo(stillingBasinSoilData?.stillingBasin?.width)}</td>
                        <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            <div className='d-flex justify-content-center'>-</div>
                        </td>
                    </tr>
                    <tr>
                        <td>{t('output_static_stepped_min_height')}</td>
                        <td>fc_min</td>
                        <td>{roundToTwo(results.data?.basinDesign.minCounterweirHeight)}</td>
                        <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            {stillingBasinSoilData?.stillingBasin?.counterweirElevation! >= roundToTwo(results.data?.basinDesign.minCounterweirHeight) ? (
                                <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />
                            ) : (
                                <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />
                            )}
                        </td>
                    </tr>
                    <tr className='bg-white'>
                        <td>{t('output_static_stepped_water_velocity')}</td>
                        <td>
                            <SmallLetter normal='v' small='1' />
                        </td>
                        <td>{roundToTwo(results.data?.basinDesign.waterVelocity)}</td>
                        <td>{conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            <div className='d-flex justify-content-center'>-</div>
                        </td>
                    </tr>
                    <tr>
                        <td>{t('output_hydraulic_vertical_stepped_sloped_lining_material')}</td>
                        <td colSpan={4}>
                            {stillingBasinSoilData?.stillingBasin?.material.name} - t={roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].thickness)}{' '}
                            {conversionLabel.m_in[projectInfo.data?.unitOfMeasure as number]} - d50=
                            {roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].rockD50)} {conversionLabel.m_in[projectInfo.data?.unitOfMeasure as number]} - Cu=
                            {roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].cu)}
                        </td>
                    </tr>
                    <tr className='bg-white'>
                        <td>{t('output_hydraulic_vertical_stepped_sloped_shear_stress_downstream_section')}</td>
                        <td>τ</td>
                        <td>{roundToTwo(results.data?.basinDesign.shearStress)}</td>
                        <td>{conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            {stillingBasinSoilData?.stillingBasin?.material.physicalData![0].allowShearStress! >= roundToTwo(results.data?.basinDesign.shearStress) ? (
                                <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />
                            ) : (
                                <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>{t('output_hydraulic_vertical_stepped_sloped_allowable_shear_stress_lining')}</td>
                        <td>
                            <SmallLetter normal='τ' small='all' />
                        </td>
                        <td>{roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].allowShearStress)}</td>
                        <td>{conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            <div className='d-flex justify-content-center'>-</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default BasinDesignStepped;
